import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, FormControl } from "react-bootstrap";
import styled from "styled-components";
import { useDebounce } from "use-debounce";
import { LogFilter, OptionMeta } from "../../../../models/LogFilter";
import { palette } from "../../../../styles/theme";
import useHighlightedItem from "../../utils/useHighlightedItem";
import SearchDropdownCheckboxItem from "./SearchDropdownCheckboxItem";
import SearchDropdownItem from "./SearchDropdownItem";
import type { Props as SearchPillProps } from "./SearchPill";

interface Props {
  /**
   * If we show check boxes + allow selecting multiple options here
   */
  allowMultiSelect: boolean;

  /**
   * "Translates" a possible enum value for the choice to make it more readable
   */
  localizeChoice: (choice: string) => string;

  /**
   * All dropdown options to render, with selection state
   */
  currentOptions: LogFilter["currentFilterOptions"] | LogFilter["currentComparators"];

  /**
   * This function selects an option from the event key passed
   */
  selectOption: (eventKey: string | null) => void;

  /**
   * A function that ends up closing this menu when the user presses ESC
   */
  closeMenu: () => void;

  /**
   * Focuses the current menu to allow us to use arrow keys
   */
  focusMenu: () => void;

  /**
   * Optional function for searching - if missing, no search bar appears
   * at the top of the dropdown.
   */
  findChoicesMatchingSearchString?: SearchPillProps["findChoicesMatchingSearchString"];

  /**
   * When true, renders a search box at the top of the dropdown and filters
   * the already-loaded options in memory as the user types. Use this when all
   * options are already loaded (e.g. the integration / organization filters).
   * For network-backed search, pass `findChoicesMatchingSearchString` instead.
   */
  enableLocalSearch?: boolean;

  /**
   * Forwarded by react-bootstrap's `Dropdown.Menu`. The menu stays mounted
   * once opened, so we use this to reset the local search text on close.
   */
  show?: boolean;

  /**
   * When true, the option labels are customer-supplied identifiers (org /
   * integration names) and are masked from PostHog capture / session replay.
   */
  isSensitive?: boolean;

  /**
   * As we pass `items` in, we never want children to exist
   */
  children?: never;
}

const Container = styled.div`
  background: ${palette.white};
  box-shadow: 0px 6px 30px -2px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 6px 0;
  outline: none;
  margin-top: 8px;
`;

// Sticky, border on the bottom, + fix spacing + border
const SearchBar = styled(FormControl)`
  position: sticky;
  border: none;
  transition: none;
  border-bottom: 1px solid ${palette.border};
  && {
    border-color: ${palette.border} !important;
  }
  border-radius: 0;
  padding: 12px;
  margin-top: -6px;
  top: -6px;
  height: fit-content;
  z-index: 1;
`;

// Just adds spacing between bottom of search bar border and next item
const Spacer = styled.div`
  height: 6px;
`;

/**
 * Creates a dropdown menu for use with react-bootstrap's
 * `Dropdown.Menu` component. Needs props to be forwarded, can
 * optionally have a search bar to filter content.
 */
const SearchDropdownMenu = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & Props
>(
  (
    {
      allowMultiSelect,
      currentOptions,
      findChoicesMatchingSearchString,
      enableLocalSearch,
      show,
      isSensitive,
      closeMenu,
      focusMenu,
      selectOption,
      localizeChoice,
      ...props
    },
    ref,
  ) => {
    const searchBarRef = useRef<HTMLInputElement>(null);

    // The raw search text, updated on every keystroke for immediate feedback
    const [searchInput, setSearchInput] = useState("");

    // Debounced search text, used only to drive the optional network-backed search
    const [debouncedSearchInput] = useDebounce(searchInput, 200);

    // Render the search box either when we can filter in memory or when a
    // network-backed search callback is provided
    const showSearchBar = enableLocalSearch || !!findChoicesMatchingSearchString;

    // Kick off the optional network-backed search once the debounced text settles
    useEffect(() => {
      if (debouncedSearchInput.trim().length > 0) {
        findChoicesMatchingSearchString?.(debouncedSearchInput);
      }
    }, [findChoicesMatchingSearchString, debouncedSearchInput]);

    // The menu stays mounted after its first open, so on close clear the search
    // text (avoids a stale filter next time), and on open refocus the box since
    // the input's autoFocus only fires on its first mount
    useEffect(() => {
      if (show) {
        searchBarRef.current?.focus();
      } else {
        setSearchInput("");
      }
    }, [show]);

    // The label the user sees for an option: the localized display name, or the
    // key as a fallback. Shared by the search filter and the render below so what
    // we match and what we show cannot drift. `displayName` may be a number (e.g.
    // response codes), so coerce it. For the Organization filter the key is an
    // opaque end-user UUID, which is why we match on the label, not the key.
    const labelForChoice = useCallback(
      (choice: string, choiceMeta: OptionMeta) =>
        localizeChoice(String(choiceMeta.displayName ?? choice)),
      [localizeChoice],
    );

    // Filter the in-memory options by the search text. When a network-backed
    // callback drives the list, the parent narrows the options, so leave
    // `currentOptions` untouched here.
    const filteredOptions = useMemo((): typeof currentOptions => {
      const query = searchInput.trim().toLowerCase();
      if (!enableLocalSearch || findChoicesMatchingSearchString || query === "") {
        return currentOptions;
      }
      return Object.fromEntries(
        Object.entries(currentOptions).filter(([choice, choiceMeta]) =>
          labelForChoice(choice, choiceMeta).toLowerCase().includes(query),
        ),
      ) as typeof currentOptions;
    }, [
      currentOptions,
      enableLocalSearch,
      findChoicesMatchingSearchString,
      labelForChoice,
      searchInput,
    ]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        // Only intercept keys while the search input has focus. Otherwise let the
        // menu's default arrow/enter/escape handling run.
        if (document.activeElement !== searchBarRef.current) {
          return false;
        }
        switch (event.key) {
          case "Escape":
            // Blur the input back to the menu so plain arrow-key nav resumes
            focusMenu();
            return true;
          case "ArrowDown":
          case "ArrowUp":
            // Stop the caret from moving in the input, then defer to menu nav
            event.preventDefault();
            return false;
          case "Enter":
            // Defer to the menu so Enter selects the highlighted option
            return false;
          default:
            // Anything else is text entry, handled by the input
            return true;
        }
      },
      [focusMenu],
    );

    /**
     * If this isn't memoized we lose state on mouse over.
     */
    const currentKeys = useMemo(() => Object.keys(filteredOptions), [filteredOptions]);

    // Uses the arrow keys and mouse over to keep track of a highlighted item
    const { propsForKey, onKeyDown } = useHighlightedItem({
      keys: currentKeys,
      selectOption,
      closeMenu,
      handleKeyDown,
      isReadonly: currentKeys.length === 0,
      // While a query is active, highlight the top match so Enter selects it
      highlightFirstOnChange: searchInput.trim().length > 0,
    });

    const emptyState =
      currentKeys.length === 0 ? (
        <Dropdown.Item as={SearchDropdownItem} {...propsForKey("")} isSelected={false}>
          <span className="text-gray-40">
            {searchInput.trim().length > 0 ? "No matches" : "No options available"}
          </span>
        </Dropdown.Item>
      ) : null;

    // use the choice directly in the dropdown item if we dont need meta structure
    return (
      <Container {...props} tabIndex={0} role="menu" ref={ref} onKeyDownCapture={onKeyDown}>
        {showSearchBar && (
          <>
            <SearchBar
              autoFocus
              ref={searchBarRef}
              aria-label="Search options"
              placeholder="Search..."
              onChange={(
                event: React.ChangeEvent<
                  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                >,
              ) => setSearchInput(event.target.value)}
              value={searchInput}
            />
            <Spacer />
          </>
        )}
        {emptyState}
        {/* Sensitive filters render customer-supplied org / integration names, so
            each option is masked at the item (ph-no-capture suppresses PostHog
            autocapture + masks it in session replay; the Frigade mirror inherits it) */}
        {Object.entries(filteredOptions).map(([choice, choiceMeta]) => (
          <Dropdown.Item
            as={allowMultiSelect ? SearchDropdownCheckboxItem : SearchDropdownItem}
            {...propsForKey(choice)}
            className={isSensitive ? "ph-no-capture" : undefined}
            isSelected={choiceMeta.isEnabled ?? choiceMeta}
          >
            {labelForChoice(choice, choiceMeta)}
          </Dropdown.Item>
        ))}
      </Container>
    );
  },
);

export default SearchDropdownMenu;
